import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, BookOpen, ChevronRight, Loader2, Sparkles, FileQuestion, RotateCcw, X, Lock } from 'lucide-react';
import { LectorTemas } from './LectorTemas';
import { FlashcardsDeck } from './FlashcardsDeck';
import {
  getTemas, watchCuestionarios,
  getAreaTemas, watchAreaCuestionarios,
  AREA_NAMES,
} from '../../services/academyFirestore';

// Áreas disponibles para el alumno. El primer tab es el contenido por
// especialidad (vortexAcademy/...) y los demás son áreas generales
// (vortexAcademyAreas/...) filtradas por la jerarquía del alumno.
const LECTOR_TABS = [
  { key: '', label: '🎯 Conocimientos Específicos' },
  { key: 'conocimientos_marineros', label: '⚓ Conocimientos Marineros' },
  { key: 'legislacion_naval', label: '📜 Legislación Naval' },
  { key: 'conocimientos_especificos', label: '🗂️ Específicos (generales)' },
  { key: 'ingles', label: '🇬🇧 Inglés' },
];

export { LECTOR_TABS as LECTOR_AREAS };

export const buildFlashcards = (quizzes) => {
  const cards = [];
  (quizzes || []).forEach((q) => {
    (q.questions || []).forEach((p, i) => {
      const correcta = p.options?.[p.correct_index];
      cards.push({
        id: `${q.id}_${i}`,
        front: p.question,
        back: `✔️ ${correcta || ''}${p.explanation ? `\n\n📖 ${p.explanation}` : ''}`,
      });
    });
  });
  return cards;
};

/* ── Ejecutor de examen (cuestionario importado) ── */
export const ExamRunner = ({ quiz, onClose, onResult }) => {
  const questions = quiz.questions || [];
  const [idx, setIdx] = useState(0);
  const [sel, setSel] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correctas, setCorrectas] = useState(0);
  const [finished, setFinished] = useState(false);
  const reportedRef = useRef(false);

  const current = questions[idx];
  const total = questions.length;

  // Reporta el resultado al aula una sola vez por intento.
  useEffect(() => {
    if (!finished || reportedRef.current || !onResult) return;
    reportedRef.current = true;
    const score = total ? Math.round((correctas / total) * 100) : 0;
    onResult({ score, passed: score >= 60, correct: correctas, total });
  }, [finished, onResult, correctas, total]);

  if (finished || !current) {
    const score = total ? Math.round((correctas / total) * 100) : 0;
    const passed = score >= 60;
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem', background: 'rgba(4,12,22,0.9)', backdropFilter: 'blur(10px)',
      }}>
        <div style={{
          background: '#0a1929', border: `1px solid ${passed ? 'rgba(52,211,153,0.4)' : 'rgba(239,68,68,0.4)'}`,
          borderRadius: '18px', width: '100%', maxWidth: '440px', padding: '2rem', textAlign: 'center',
          display: 'flex', flexDirection: 'column', gap: '14px',
        }}>
          <div style={{ fontSize: '2.4rem', fontWeight: 800, color: passed ? '#34d399' : '#f87171' }}>{score}%</div>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#cbd5e1' }}>
            Acertaste <strong style={{ color: '#fff' }}>{correctas} de {total}</strong> preguntas.
          </p>
          <p style={{ margin: 0, fontSize: '0.8rem', color: passed ? '#34d399' : '#f87171', fontWeight: 700 }}>
            {passed ? '✅ ¡Aprobado!' : 'Sigue practicando para mejorar.'}
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button type="button" onClick={() => { setIdx(0); setSel(null); setAnswered(false); setCorrectas(0); setFinished(false); reportedRef.current = false; }} style={{ ...btnGhost, color: 'var(--gold-light)' }}>
              <RotateCcw size={14} /> <span>Repetir</span>
            </button>
            <button type="button" onClick={onClose} style={{ ...btnGhost, color: '#94a3b8' }}>
              <X size={14} /> <span>Cerrar</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const select = (i) => { if (!answered) setSel(i); };
  const confirmar = () => {
    if (sel === null || answered) return;
    setAnswered(true);
    if (sel === current.correct_index) setCorrectas((c) => c + 1);
  };
  const next = () => {
    if (idx + 1 >= total) setFinished(true);
    else { setIdx((i) => i + 1); setSel(null); setAnswered(false); }
  };

  const optionStyle = (i) => {
    const base = {
      display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', cursor: 'pointer',
      padding: '11px 14px', borderRadius: '10px',
      background: 'rgba(255,255,255,0.03)', color: '#e2e8f0', fontSize: '0.82rem', fontFamily: 'var(--font-primary)',
    };
    if (!answered) return { ...base, background: sel === i ? 'rgba(197,160,89,0.15)' : 'rgba(255,255,255,0.03)', border: sel === i ? '1px solid rgba(197,160,89,0.5)' : '1px solid rgba(255,255,255,0.1)' };
    if (i === current.correct_index) return { ...base, border: '1px solid rgba(52,211,153,0.5)', background: 'rgba(52,211,153,0.1)' };
    if (i === sel) return { ...base, border: '1px solid rgba(239,68,68,0.5)', background: 'rgba(239,68,68,0.1)' };
    return { ...base, opacity: 0.5, border: '1px solid rgba(255,255,255,0.1)' };
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', background: 'rgba(4,12,22,0.9)', backdropFilter: 'blur(10px)',
    }}>
      <div style={{
        background: '#0a1929', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '18px',
        width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto', padding: '22px',
        display: 'flex', flexDirection: 'column', gap: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FileQuestion size={16} style={{ color: '#f59e0b' }} />
          <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {quiz.title || 'Examen'}
          </span>
          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Pregunta {idx + 1} de {total}</span>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ margin: 0, fontSize: '0.95rem', color: '#f1f5f9', lineHeight: 1.5, fontWeight: 600 }}>{current.question}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {(current.options || []).map((opt, i) => (
            <button key={i} type="button" onClick={() => select(i)} style={optionStyle(i)} disabled={answered}>
              <span style={{
                width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0, fontSize: '0.7rem', fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(197,160,89,0.12)', color: 'var(--gold-light)',
              }}>
                {String.fromCharCode(65 + i)}
              </span>
              <span>{opt}</span>
            </button>
          ))}
        </div>

        {answered && current.explanation && (
          <div style={{
            borderRadius: '10px', padding: '12px 14px', fontSize: '0.78rem', lineHeight: 1.55, color: '#cbd5e1',
            background: 'rgba(197,160,89,0.08)', border: '1px solid rgba(197,160,89,0.25)',
          }}>
            <strong style={{ color: '#93c5fd' }}>Explicación:</strong> {current.explanation}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {!answered ? (
            <button type="button" onClick={confirmar} disabled={sel === null} style={{ ...btnPrimary, opacity: sel === null ? 0.5 : 1 }}>
              Confirmar respuesta
            </button>
          ) : (
            <button type="button" onClick={next} style={btnPrimary}>
              {idx + 1 >= total ? 'Ver resultado' : 'Siguiente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const btnPrimary = {
  background: 'linear-gradient(135deg, var(--gold-matte) 0%, #dbb96a 100%)', color: '#fff', border: 'none',
  borderRadius: '10px', padding: '9px 18px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: '7px', fontFamily: 'var(--font-primary)',
};

const btnGhost = {
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '10px', padding: '8px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: '7px', fontFamily: 'var(--font-primary)',
};

/**
 * Vista del Lector de Temas para el alumno.
 * Ofrece 4 pestañas: Conocimientos Específicos (por especialidad, desde
 * vortexAcademy/...) y las áreas generales Marineros / Legislación /
 * Específicos generales (desde vortexAcademyAreas/..., filtradas por la
 * jerarquía del alumno con el campo `grados` de cada tema).
 * Cada tema abre tres pestañas: Lectura, Examen y Flashcards.
 */
export const LectorTemasView = ({ academyProfile, onBack, backLabel = 'Volver al Catálogo', isSubscribed = true }) => {
  const [selTab, setSelTab] = useState(''); // key del área ('' = por especialidad)
  const [temas, setTemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTema, setActiveTema] = useState(null); // { areaKey, tema }
  const [tab, setTab] = useState('lectura'); // 'lectura' | 'examen' | 'flashcards'
  const [cuestionarios, setCuestionarios] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);

  const jerarquia = academyProfile?.jerarquia || '';
  const cuerpo = academyProfile?.cuerpoServicio || '';
  const especialidad = academyProfile?.especialidad || '';

  // Temas según el área seleccionada
  useEffect(() => {
    let cancelled = false;
    setError('');
    setActiveTema(null);
    setActiveQuiz(null);
    setTab('lectura');

    // Área general: requiere solo jerarquía, filtrado por `grados`
    if (selTab) {
      if (!jerarquia) { setTemas([]); setLoading(false); return undefined; }
      setLoading(true);
      getAreaTemas(selTab)
        .then((list) => {
          if (cancelled) return;
          const visibles = list.filter(
            (t) => t.grados.includes('TODOS') || t.grados.includes(jerarquia)
          );
          setTemas(visibles);
        })
        .catch((e) => { if (!cancelled) setError('Error cargando el lector: ' + e.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }

    // Conocimientos específicos: por especialidad (jerarquía + cuerpo + especialidad)
    if (!jerarquia || !cuerpo || !especialidad) {
      setTemas([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getTemas({ jerarquia, cuerpo, especialidad })
      .then((list) => { if (!cancelled) setTemas(list); })
      .catch((e) => { if (!cancelled) setError('Error cargando el lector: ' + e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selTab, jerarquia, cuerpo, especialidad]);

  // Cuestionarios (exámenes) del tema activo
  useEffect(() => {
    if (!activeTema) { setCuestionarios([]); setTab('lectura'); return undefined; }
    const { areaKey, tema } = activeTema;
    if (areaKey) {
      return watchAreaCuestionarios(areaKey, tema.id, (list) => setCuestionarios(list));
    }
    return watchCuestionarios({ jerarquia, cuerpo, especialidad, temaId: tema.id }, (list) => setCuestionarios(list));
  }, [activeTema, jerarquia, cuerpo, especialidad]);

  const flashcards = buildFlashcards(cuestionarios);
  const activeT = activeTema?.tema || null;

  // Defensa en profundidad: sin licencia activa en licencesPremium no se
  // renderiza ningún tema, examen ni flashcards.
  if (!isSubscribed) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '440px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={32} style={{ color: 'var(--gold-matte)' }} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0 }}>Lector de Temas bloqueado</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            Necesitas una licencia activa en <strong style={{ color: '#fff' }}>licencesPremium</strong> para usar el lector de temas.
          </p>
          <button type="button" onClick={onBack} className="btn-vx-action" style={{ alignSelf: 'center', padding: '10px 20px' }}>
            <ArrowLeft size={16} />
            <span>{backLabel}</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Examen activo ──
  if (activeQuiz && activeT) {
    return (
      <>
        <ExamRunner quiz={activeQuiz} onClose={() => setActiveQuiz(null)} />
        <LectorTemas
          courseTitle={`⚓ ${activeT.title}`}
          moduleLabel={activeT.module}
          lessons={activeT.lessons}
        />
      </>
    );
  }

  // ── Lectura activa ──
  if (activeTema && activeT) {
    const tabBtn = (id, label, count) => (
      <button
        key={id}
        type="button"
        onClick={() => setTab(id)}
        style={{
          padding: '9px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700,
          fontFamily: 'var(--font-primary)',
          border: tab === id ? '1px solid rgba(197,160,89,0.5)' : '1px solid rgba(255,255,255,0.1)',
          background: tab === id ? 'rgba(197,160,89,0.15)' : 'rgba(255,255,255,0.03)',
          color: tab === id ? 'var(--gold-light)' : '#94a3b8',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}
      >
        {label}
        {typeof count === 'number' && <span style={{ fontSize: '0.66rem', opacity: 0.8 }}>({count})</span>}
      </button>
    );

    return (
      <>
        <button
          type="button"
          onClick={() => setActiveTema(null)}
          style={{
            position: 'fixed', top: '14px', left: '14px', zIndex: 60,
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '10px', cursor: 'pointer',
            border: '1px solid rgba(197,160,89,0.4)', background: 'rgba(10,26,42,0.92)',
            color: '#c5a059', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-primary)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <ArrowLeft size={14} />
          <span>Lista de temas</span>
        </button>

        <div style={{
          position: 'fixed', top: '14px', left: '50%', transform: 'translateX(-50%)', zIndex: 60,
          display: 'flex', gap: '8px', padding: '6px', borderRadius: '12px',
          background: 'rgba(10,26,42,0.92)', border: '1px solid rgba(197,160,89,0.3)', backdropFilter: 'blur(6px)',
        }}>
          {tabBtn('lectura', '📖 Lectura')}
          {tabBtn('examen', '📝 Examen', cuestionarios.length)}
          {tabBtn('flashcards', '🗂️ Flashcards', flashcards.length)}
        </div>

        {tab === 'lectura' && (
          <LectorTemas
            courseTitle={`⚓ ${activeT.title}`}
            moduleLabel={activeT.module}
            lessons={activeT.lessons}
          />
        )}

        {tab === 'examen' && (
          <div style={{ minHeight: '100vh', background: '#0a1a2a', paddingTop: '76px' }}>
            <div style={{ maxWidth: '820px', margin: '0 auto', padding: '24px 24px 60px' }}>
              <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.2rem', color: '#fff', margin: '0 0 4px' }}>
                Exámenes · {activeT.title}
              </h2>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 18px' }}>
                Cuestionarios generados para este tema.
              </p>
              {cuestionarios.length === 0 ? (
                <div style={{ background: 'rgba(10,26,42,0.7)', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '16px', padding: '2.5rem 2rem', textAlign: 'center' }}>
                  <FileQuestion size={30} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
                  <p style={{ margin: '10px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    Este tema aún no tiene exámenes disponibles.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {cuestionarios.map((quiz) => (
                    <button
                      key={quiz.id}
                      type="button"
                      onClick={() => setActiveQuiz(quiz)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left', cursor: 'pointer',
                        padding: '16px 18px', borderRadius: '14px',
                        background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                        background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <FileQuestion size={18} style={{ color: '#f59e0b' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>{quiz.title}</p>
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                          {quiz.questions?.length || 0} preguntas
                        </span>
                      </div>
                      <ChevronRight size={18} style={{ color: '#64748b', flexShrink: 0 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'flashcards' && (
          <div style={{ minHeight: '100vh', background: '#0a1a2a', paddingTop: '76px' }}>
            <div style={{ maxWidth: '820px', margin: '0 auto', padding: '24px 24px 60px' }}>
              <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.2rem', color: '#fff', margin: '0 0 4px' }}>
                Flashcards · {activeT.title}
              </h2>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 18px' }}>
                Repasa pregunta-respuesta derivadas de los exámenes del tema.
              </p>
              {flashcards.length === 0 ? (
                <div style={{ background: 'rgba(10,26,42,0.7)', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '16px', padding: '2.5rem 2rem', textAlign: 'center' }}>
                  <Sparkles size={30} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
                  <p style={{ margin: '10px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    No hay flashcards disponibles para este tema todavía.
                  </p>
                </div>
              ) : (
                <FlashcardsDeck flashcards={flashcards} />
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // ── Sin jerarquía (necesaria para cualquier área) ──
  if (!jerarquia) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '480px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={32} style={{ color: 'var(--gold-matte)' }} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0 }}>Lector de Temas</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            Configura tu perfil Vortex Academy (jerarquía, cuerpo y especialidad) para ver tus páginas de lectura.
          </p>
          <button type="button" onClick={onBack} className="btn-vx-back" style={{ alignSelf: 'center' }}>
            <ArrowLeft size={16} />
            <span>{backLabel}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a1a2a' }}>
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '40px 24px 60px' }}>
        <button type="button" onClick={onBack} className="btn-vx-back" style={{ marginBottom: '1.5rem' }}>
          <ArrowLeft size={16} />
          <span>{backLabel}</span>
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--gold-matte)', fontFamily: 'monospace', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            📖 Lector de Temas · Vortex Academy
          </span>
          <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            {selTab ? AREA_NAMES[selTab] : 'Páginas de lectura de tu especialidad'}
          </h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
            {jerarquia}{cuerpo ? ` · ${cuerpo}` : ''}{especialidad ? ` · ${especialidad}` : ''}
          </p>
        </div>

        {/* Pestañas de área */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '1.5rem' }}>
          {LECTOR_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSelTab(t.key)}
              style={{
                padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700,
                fontFamily: 'var(--font-primary)',
                border: selTab === t.key ? '1px solid rgba(197,160,89,0.5)' : '1px solid rgba(255,255,255,0.1)',
                background: selTab === t.key ? 'rgba(197,160,89,0.12)' : 'rgba(255,255,255,0.03)',
                color: selTab === t.key ? '#e3c07b' : '#94a3b8',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {selTab && (
          <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '0 0 1rem', lineHeight: 1.5 }}>
            Mostrando solo los temas asignados a tu jerarquía (<strong style={{ color: 'var(--text-secondary)' }}>{jerarquia}</strong>).
          </p>
        )}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '0.82rem', padding: '1rem' }}>
            <Loader2 className="w-5 h-5" />
            Cargando páginas de lectura...
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fecaca', borderRadius: '12px', padding: '14px 16px', fontSize: '0.8rem' }}>
            {error}
          </div>
        )}

        {!loading && !error && !selTab && (!cuerpo || !especialidad) && (
          <div style={{ background: 'rgba(10,26,42,0.7)', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <Sparkles size={32} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Completa tu cuerpo/servicio y especialidad para ver los Conocimientos Específicos de tu perfil.
              <br />Las áreas generales (Marineros y Legislación) ya están disponibles arriba.
            </p>
          </div>
        )}

        {!loading && !error && temas.length === 0 && (selTab || (cuerpo && especialidad)) && (
          <div style={{ background: 'rgba(10,26,42,0.7)', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <Sparkles size={32} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {selTab
                ? 'Aún no hay temas de esta área asignados a tu jerarquía. Contacta a tu administrador.'
                : 'Aún no hay páginas de lectura publicadas para tu especialidad.\nVuelve pronto o contacta a tu administrador.'}
            </p>
          </div>
        )}

        {!loading && !error && temas.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {temas.map((tema) => (
              <button
                key={tema.id}
                type="button"
                onClick={() => setActiveTema({ areaKey: selTab, tema })}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left', cursor: 'pointer',
                  padding: '16px 18px', borderRadius: '14px',
                  background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(197,160,89,0.5)'; e.currentTarget.style.background = 'rgba(197,160,89,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}
              >
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                  background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <BookOpen size={18} style={{ color: 'var(--gold-light)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>{tema.title}</p>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                    {tema.lessons.length} lecciones{tema.module ? ` · ${tema.module}` : ''}
                    {tema.cuestionariosCount > 0 ? ` · 🎯 ${tema.cuestionariosCount} exámenes` : ''}
                  </span>
                </div>
                <ChevronRight size={18} style={{ color: '#64748b', flexShrink: 0 }} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
